import { useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { url, breadcrumbSchema } from "../data/site";
import { STATES, REGIONS, NO_WAGE_TAX, LOCAL_TAX, PAID_LEAVE, stateSlug, sortedAbbrs, joinNames } from "../data/states";

const trail = [["Home", "/"], ["States", "/payroll-services"]];

export default function StatesHub() {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("All");

  const visible = sortedAbbrs.filter((a) => {
    const inRegion = region === "All" || REGIONS[region].includes(a);
    const matches = STATES[a][0].toLowerCase().includes(query.toLowerCase());
    return inRegion && matches;
  });

  const itemList = {
    "@context": "https://schema.org", "@type": "ItemList", name: "ABSLpay payroll services by state",
    itemListElement: sortedAbbrs.map((a, i) => ({
      "@type": "ListItem", position: i + 1, name: `${STATES[a][0]} payroll services`,
      url: url(`/payroll-services/${stateSlug(a)}`),
    })),
  };

  const noTax = sortedAbbrs.filter((a) => NO_WAGE_TAX.has(a));
  const local = joinNames(sortedAbbrs.filter((a) => LOCAL_TAX.has(a)).map((a) => STATES[a][0]));
  const leave = joinNames(sortedAbbrs.filter((a) => PAID_LEAVE.has(a)).map((a) => STATES[a][0]));

  return (
    <>
      <Seo
        title="Payroll Services by State: All 50 States + DC | ABSLpay"
        description="Full-service payroll in every U.S. state and Washington, DC. State income tax, unemployment insurance, local taxes and paid-leave programs handled, from $39/mo."
        path="/payroll-services"
        jsonLd={[itemList, breadcrumbSchema(trail)]}
      />
      <Breadcrumbs trail={trail} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,#ECFDF5_0%,transparent_50%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 lg:pt-16">
          <MaskedLines
            lines={["Payroll services in", "all 50 states and DC"]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
          />
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600">
            One price wherever your people work. We register state accounts, withhold the right taxes and file every
            return, whether you pay one person in one state or a remote team across the country.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="state-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your state..."
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {["All", ...Object.keys(REGIONS)].map((r) => (
                <button
                  key={r}
                  data-testid={`region-filter-${r.toLowerCase()}`}
                  onClick={() => setRegion(r)}
                  className={`rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${
                    region === r ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-900"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="states-grid">
            {visible.map((a, i) => (
              <Reveal key={a} delay={Math.min(i * 0.02, 0.3)} y={14}>
                <Link
                  to={`/payroll-services/${stateSlug(a)}`}
                  data-testid={`state-card-${stateSlug(a)}`}
                  className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 transition-colors hover:border-emerald-500 hover:bg-emerald-50/40"
                >
                  <span className="text-sm font-semibold text-slate-800 group-hover:text-emerald-800">{STATES[a][0]}</span>
                  <span className="font-mono-num text-xs font-bold text-slate-400 group-hover:text-emerald-600">{a}</span>
                </Link>
              </Reveal>
            ))}
            {visible.length === 0 && <p className="col-span-full text-sm text-slate-500">No states match that search.</p>}
          </div>
        </div>
      </section>

      <section className="bg-white" id="no-income-tax">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">How state payroll differs</h2>
            <div className="mt-8 space-y-6 text-base leading-relaxed text-slate-600">
              <p>
                <strong className="text-slate-900">No state income tax on wages:</strong>{" "}
                {noTax.map((a, i) => (
                  <span key={a}>
                    {i > 0 && (i === noTax.length - 1 ? " and " : ", ")}
                    <Link to={`/payroll-services/${stateSlug(a)}`} className="font-semibold text-emerald-700 hover:underline">{STATES[a][0]}</Link>
                  </span>
                ))}
                . Employers here skip state withholding but still file unemployment insurance and new-hire reports.
              </p>
              <p><strong className="text-slate-900">Local payroll taxes in some areas:</strong> {local}.</p>
              <p><strong className="text-slate-900">Payroll-funded paid leave or disability programs:</strong> {leave}.</p>
              <p className="text-sm text-slate-400">Summaries are general and rules change. Your state page lists what we handle there.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
